/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class QueryDefinitionAPI
 * @hideconstructor
 * @classdesc API class which describes a query in a manner abstracted from a visualization and from the query protocol
 */
define([], () => {
	class QueryDefinitionAPI {

		/**
		 * @return {String} the ID of this definition (e.g. `data`)
		 */
		getId() {}

		/**
		 * @return {String} the type of this definition (e.g. `main`)
		 */
		getType() {}

		/**
		 * @function QueryDefinitionAPI#setDataSource
		 * @description Set the data source for this query
		 * @public
		 * @param {DataSourceAPI} sourceId
		 */
		setDataSource(source) {
			void(source);
		}

		/**
		 * @function QueryDefinitionAPI#getDataSource
		 * @description Get data source
		 * @public
		 * @return {DataSourceAPI}
		 */
		getDataSource() {}

		/**
		 * Set summaries to be shown or hidden.
		 * @param {Boolean} flag - true to enable summaries.
		 */
		setSummariesEnabled() {}

		/**
		 * @function QueryDefinitionAPI#getSummariesEnabled
		 * @description return true if summaries are enabled (applies to crosstabs and tables)
		 * @public
		 * @return {Boolean}
		 */
		getSummariesEnabled() {}

		/**
		 * Set suppression to be on or off.
		 * @param {Boolean} flag - true to enable suppression.
		 */
		setSuppressionEnabled() {}

		/**
		 * @function QueryDefinitionAPI#getSuppressionEnabled
		 * @description return true if suppression is enabled (applies to crosstabs and tables)
		 * @public
		 * @return {Boolean}
		 */
		getSuppressionEnabled() {}

		/**
		 * @function QueryDefinitionAPI#createDataItemSet
		 * @description Creates and returns a QueryDataItemSetAPI object in user in QueryDefinitionAPIs
		 * @public
		 *
		 * @param {DataItemAPI[]} dataItems An array of one or more data items to initialize the QueryDataItemSetAPI with
		 * @return {QueryDataItemSetAPI}
		 */
		createDataItemSet() {}

		/**
		 * Get all data item sets to query
		 * @return {QueryDataItemSetAPI[]}
		 */
		getDataItemSetList() {}

		/**
		 * Get all data items in this set and its descendants
		 * @return {QueryDataItemAPI[]}
		 */
		getAllDataItemsList() {}

		/**
		 * Add filters
		 * @param {object} array of filters
		 */
		addFilters() {}

		/**
		 * Add parameter values
		 * @param {object[]} array of parameter values used by query
		 */
		addParameterValues() {}

		/**
		 * Get parameter values
		 * @return {object[]} array of parameter values in this query definition
		 */
		getParameterValues() {}

		/**
		 * Remove filters
		 * @param {object} array of filters to be removed
		 */
		removeFilters() {}

		/**
		 * Get the list of filters stored in this query definition
		 * @return {object} array of filters
		 */
		getFilters() {}

		/**
		 * @function QueryDefinitionAPI#addQueryHint
		 * @description Add a query hint into definition
		 * @public
		 * @param {Object} query hint {key, spec}
		 */
		addQueryHint() {}

		/**
		 * @function QueryDefinitionAPI#getQueryHints
		 * @description Get query hints
		 * @public
		 * @return {Array} query hints
		 */

		getQueryHints() {}

		/**
		 * Set expand to be shown or hidden (applies to crosstabs).
		 * @param {Boolean} flag - true to enable expand
		 */
		setExpandEnabled() {}

		/**
		 * @function QueryDefinitionAPI#getExpandEnabled
		 * @description return true if expand is enabled (applies to crosstabs)
		 * @public
		 * @return {Boolean}
		 */
		getExpandEnabled() {}

		/**
		 * @function QueryDefinitionAPI#setSupportsNonContextualTopBottom
		 * @param {Boolean} - set to false if this visualization does not support NonContextualTop/Bottom
		 */
		setSupportsNonContextualTopBottom() {}

		/**
		 * @function QueryDefinitionAPI#getExpandEnabled
		 * @description return true if NonContextualTopBottom is enabled (usu: false for crosstab and list)
		 * @public
		 * @return {Boolean}
		 */
		getSupportsNonContextualTopBottom() {}

		/**
		 * @public
		 * @function QueryDefinitionAPI#getPropertyValue
		 * @description Get a query definition property value
		 * @param {String} name unique name of the property
		 * @return {Object} property value
		 */
		getPropertyValue() {}

		/**
		 * @public
		 * @function QueryDefinitionAPI#setPropertyValue
		 * @description Set a query definition property value of the given property name
		 * @param {String} name unique name of the property
		 * @param {Object} value new value of the property
		 */
		setPropertyValue() {}
	}

	return QueryDefinitionAPI;

});
